/**
 * @module Select
 */

/**
 * Пункт option селекта.
 * @typedef {Object} Choice
 *
 * @property {string} value Значение атрибута value.
 * @property {string} label Отображаемый текст.
 * @property {boolean} selected Пункт выбран.
 * @property {boolean} disabled Пункт заблокирован.
 */

/* Внутри Choices не добавляются полифиллы, так что их нужно добавлять вручную. Полифиллы core-js добавляются через babel-polyfill, если они используются в нашем коде (не вендоре). Array.from на текущий момент не используется, так что добавляется напрямую. CustomEvent не входит в core-js, так что для него отдельный полифилл. */
import 'core-js/features/array/from';
import 'custom-event-polyfill';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.css';
import { toArray, lang } from '../../../scripts/helpers';
import choiceTemplate from './templates/choice.hbs';
import itemTemplate from './templates/item.hbs';
import Component from '../Component';
import './choices.css';
import './styles.css';

/**
 * Селект.
 *
 * Использует плагин Choices.
 * Для прямого доступа к функционалу плагина используется поле instance.
 *
 * Для удобства работы с типовыми задачами прописан ряд методов:
 * Через addChoices можно добавлять новые пункты в конец списка, либо переопределять весь список.
 * Добавлять в середину, видоизменять существующие или удалять отдельные пункты возможности нет.
 * Также плагин не предоставляет возможности получить информацию о текущем списке объектов choices, чтобы его впоследствии дополнить.
 * select/unselect позволяют управлять выбором пунктов, если известны их value.
 * reset просто сбрасывает любой выбор.
 * getValue позволяет получить значения value выбранных пунктов.
 *
 * customRender предоставляет интерфейс для кастомного рендера (добавление разметки внутрь пункта).
 * Этот метод не вызывается напрямую, только внутри init, т.к. должен быть вызван до создания instance плагина.
 * Чтобы воспользоваться кастомным рендером, нужно передать два аргумента в метод init().
 * Первым аргументом рендер-функции createChoiceTemplate и createItemTemplate.
 * Вторым аргументом массив choices для определения данных и searchFields для работы поиска.
 * В choices через customProperties можно передать любые данные, а основные: value, label, selected и disabled.
 * Все эти данные при рендере будут доступны через параметр data.
 *
 * @see {@link https://github.com/jshjohnson/Choices}
 *
 * @todo Не происходит скролла к ошибке, т.к. валидируется невидимый элемент.
 * @todo При кастомном рендере одиночного селекта не срабатывает selected, заданный из настроек рендера.
 * @todo A11y тесты ссылаются на отсутствие лейбла у инпута. Как мы можем повлиять на разметку плагина неясно.
 */
export default class Select extends Component {
    static className = 'select';

    initElements() {
        this.elements = {
            $select: $('.select__input', this.$block),
        };
    }

    setTemplateProperties() {
        /**
         * Множественный селект.
         * @type {boolean}
         */
        this.isMultiple = this.elements.$select.is('[multiple]');

        /**
         * Альтернативный текст плейсхолдера.
         * @type {string}
         */
        this.placeholder = this.$block.attr('data-placeholder');
    }

    setProperties({ isSearch = true, max = -1, createChoiceTemplate, createItemTemplate }) {
        /**
         * Настройки плагина
         * @type {Object}
         */
        this.options = {
            maxItemCount: max,
            searchEnabled: isSearch,
            /* Для множественного селекта включены кнопки удаления выбранных элементов. Для одиночного эту функцию выполняет плейсхолдер. */
            removeItemButton: this.isMultiple,
            /* Отключает сортировку элементов, сохраняя исходный порядок. */
            shouldSort: false,
            /* Остальные параметры отвечают за определение текста в зависимости от локализации. */
            searchPlaceholderValue: lang() === 'ru' ? 'Найти...' : 'Search...',
            loadingText: lang() === 'ru' ? 'Загрузка...' : 'Loading...',
            noResultsText: lang() === 'ru' ? 'Ничего не найдено' : 'No results found',
            noChoicesText: lang() === 'ru' ? 'Не осталось вариантов для выбора' : 'No choices to choose from',
            itemSelectText: lang() === 'ru' ? 'Нажмите для выбора' : 'Press to select',
            maxItemText: maxItemCount =>
                lang() === 'ru'
                    ? `Только ${maxItemCount} значения могут быть добавлены`
                    : `Only ${maxItemCount} values can be added`,
        };

        if (this.placeholder) {
            this.options.placeholderValue = this.placeholder;
        } else {
            this.options.placeholderValue = lang() === 'ru' ? 'Выберите вариант...' : 'Choose option...';
        }

        /**
         * Функция кастомного рендера choice.
         * @type {Function}
         */
        this.createChoiceTemplate = createChoiceTemplate;

        /**
         * Функция кастомного рендера item.
         * @type {Function}
         */
        this.createItemTemplate = createItemTemplate;
    }

    initPlugin(pluginOptions) {
        Object.assign(this.options, pluginOptions);

        /* Если через create() были переданы функции кастомного рендера, то рендерим. */
        if (this.createChoiceTemplate && this.createItemTemplate) {
            this.customRender(this.createChoiceTemplate, this.createItemTemplate);
        }

        /**
         * Объект для доступа к функционалу плагина.
         * @type {Object}
         */
        this.instance = new Choices(this.elements.$select[0], this.options);
    }

    /**
     * Производит кастомный рендер.
     *
     * Эта функция представляет из себя обёртку для упрощения кастомного рендера.
     * Она производит присвоение коллбека с переопределением стандартной структуры элементов плагина:
     * https://github.com/jshjohnson/Choices/blob/master/src/scripts/templates.js
     * Это позволяет задать кастомный рендер передачей двух функций, возвращающих шаблоны внутреннего содержимого choice и item.
     *
     * @example <caption>Пример передаваемой функции</caption>
     * // Через data осуществляется доступ к данным по choices, заданным при инициализации.
     * createChoiceTemplate = data => `
     *      <span class="select__choice-name">${data.customProperties.name} ${data.customProperties.lastName}</span>
     * `;
     *
     * @param {Function} createChoiceTemplate Функция, возвращающая шаблон choice.
     * @param {Function} createChoiceTemplate Функция, возвращающая шаблон item.
     */
    customRender(createChoiceTemplate, createItemTemplate) {
        const self = this;

        /**
         * Обработчик создания шаблонов.
         *
         * @param {Function} template Функция шаблонизатора для оборачивания разметки.
         */
        this.options.callbackOnCreateTemplates = template => ({
            /**
             * Задаёт разметку элемента селекта (option).
             *
             * @param  {Object} classNames Объект с необходимыми классами.
             * @param  {Object} data Объект с данными элемента.
             * @return {Object} Разметка выбранного элемента.
             */
            choice(classNames, data) {
                let innerTpl;

                if (!data.placeholder) {
                    innerTpl = createChoiceTemplate(data);
                }

                return template(
                    choiceTemplate({
                        classNames,
                        data,
                        innerTpl,
                    })
                );
            },
            /**
             * Задаёт разметку выбранного элемента.
             *
             * @param  {Object} classNames Объект с необходимыми классами.
             * @param  {Object} data Объект с данными элемента.
             * @return {Object} Разметка выбранного элемента.
             */
            item(classNames, data) {
                const { isMultiple } = self;

                let innerTpl;

                if (!data.placeholder) {
                    innerTpl = createItemTemplate(data);
                }

                return template(
                    itemTemplate({
                        classNames,
                        data,
                        innerTpl,
                        isMultiple,
                    })
                );
            },
        });
    }

    /**
     * Добавляет новые пункты в селект.
     *
     * @param {Choice | Choice[]} choices Пункт или массив пунктов для добавления.
     * @param {boolean} [isReplace=false] Перезаписать существующие пункты.
     */
    addChoices(choices, isReplace = false) {
        const choicesArr = toArray(choices);

        this.instance.setChoices(choicesArr, 'value', 'label', isReplace);
    }

    /**
     * Выбирает пункты с переданными значениями value.
     *
     * @param {string | string[]} value Значение или массив значений value атрибутов.
     */
    select(value) {
        this.instance.setChoiceByValue(value);
    }

    /**
     * Снимает выбор с пунктов с переданными значениями value.
     *
     * @param {string | string[]} value Значение или массив значений value атрибутов.
     */
    unselect(value) {
        const valuesArr = toArray(value);

        valuesArr.forEach(val => this.instance.removeActiveItemsByValue(val));

        /* Для одиночных селектов, если выбранных значений нет, то ставим плейсхолдер. */
        if (!this.isMultiple && !this.getValue()) {
            this.select('');
        }
    }

    /**
     * Возвращает выбранные value.
     *
     * @return {string | string[]} Значение или массив значений value.
     */
    getValue() {
        return this.instance.getValue(true);
    }

    /**
     * Отменяет выбор options.
     */
    reset() {
        this.instance.removeActiveItems();

        /* Для одиночных селектов, ставим плейсхолдер. */
        if (!this.isMultiple) {
            this.select('');
        }
    }
}

$(document).on('change', '.select__input', handleSelectChange);

/**
 * Обработчик изменения селекта.
 *
 * @param {Object} e Объект события.
 */
function handleSelectChange(e) {
    const { currentTarget } = e;
    const $select = $(currentTarget);

    /* После изменения кастомных объектов плагина триггерится это событие, где мы можем вручную запустить валидацию на исходном селекте. */
    if ($select.parsley) {
        $select.parsley().validate();
    }
}

/**
 *
 * Определяет вверх или вниз показывать селект
 */

window.addEventListener('load', () => {
    const selects = document.querySelectorAll('.choices');

    selects.forEach(el => {
        el.addEventListener('click', () => {
            // if dropdown visible, exit from func
            const dropdown = el.querySelector('.choices__list--dropdown');

            if (dropdown.classList.contains('is-active')) {
                return;
            }

            // calculate height of hidden dropdown
            dropdown.classList.add('js-get-size');
            const dropdownHeight = dropdown.offsetHeight;
            dropdown.classList.remove('js-get-size');
            // calculate height of hidden dropdown

            const selector = el.querySelector('.choices__inner');
            const screenHeight = window.screen.height;
            const selectorCoords = selector.getBoundingClientRect();
            const totalHeight = selectorCoords.top + dropdownHeight;
            const offsetMultiplier = dropdown.querySelector('.choices__list').childNodes.length;

            if (totalHeight > screenHeight) {
                dropdown.style.top = `-${offsetMultiplier * 100}%`;
            } else {
                dropdown.style.top = '100%';
            }
        });
    });
});
